import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import {
  Compass, MapPin, Plant, Drop, Sun, Cloud, Wind, Sparkle,
  ArrowRight, TrendUp, Coins, Tree, Heart, ArrowClockwise,
  CircleNotch, Butterfly, Recycle,
} from "@phosphor-icons/react";
import LocationPicker from "@/components/LocationPicker";
import DualExplainer from "@/components/DualExplainer";
import GlossaryTerm from "@/components/GlossaryTerm";
import { fetchLocationSnapshot } from "@/lib/api";
import {
  ndviAnomaly, soilDeficitStatus, yieldStatus, toneColor,
} from "@/lib/laymen";

function StatCell({ icon: Icon, label, value, level = "ok", small }) {
  const t = toneColor(level);
  return (
    <div
      className="p-3 rounded-sm"
      style={{ backgroundColor: t.bg, color: t.fg }}
      data-testid={`stat-${label.toLowerCase().replace(/ /g, "-")}`}
    >
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider opacity-80">
        <Icon size={11} /> {label}
      </div>
      <div className={small ? "text-base font-medium mt-0.5" : "text-xl font-medium mt-1 tracking-tight"}>
        {value}
      </div>
    </div>
  );
}

/**
 * Inspiring impact card — translates cold metrics into human stories.
 * Klima · Gewinn · Tier · Wasser · Kreislauf
 */
function ImpactCard({ snap }) {
  const m = snap?.metrics || {};
  const ndvi = m.ndvi_latest ?? null;
  const smDef = m.soil_moisture_deficit ?? null;
  const yieldIdx = m.yield_forecast_index ?? null;

  // Simple translations (order: positive first, motivational)
  const ndviLine = ndvi == null
    ? "Satellit sammelt gerade neue Daten für deine Region."
    : ndvi > 0.55
      ? `🌿 Dein Bestand ist grün wie ein Maitag — NDVI ${ndvi.toFixed(2)}.`
      : ndvi > 0.35
        ? `🌱 Solider Grünanteil (NDVI ${ndvi.toFixed(2)}) — deine Fläche arbeitet.`
        : `🌾 Leicht kahle Flächen erkennbar (NDVI ${ndvi.toFixed(2)}). Kein Grund zur Sorge — wir beobachten weiter.`;

  const waterLine = smDef == null
    ? "Wir holen gerade die aktuellste Bodenfeuchte vom Open-Meteo-Modell."
    : smDef <= 0
      ? `💧 Der Boden hat genug Wasser — eine Flasche pro Quadratmeter gespart gegenüber Bewässerung.`
      : smDef <= 0.03
        ? `💧 Leichtes Wasserdefizit — eine Bewässerung in den nächsten Tagen lohnt sich.`
        : `☀️ Trockenheit drückt — jetzt handeln schützt deine Kulturen.`;

  const yieldLine = yieldIdx == null
    ? ""
    : yieldIdx >= 60
      ? `💶 Mit aktuellen Preisen deutet alles auf eine solide Saison hin.`
      : yieldIdx >= 40
        ? `📊 Ernte durchschnittlich. Gute Zeit für Feldbegehung & Detail-Planung.`
        : `🎯 Die Zahlen zeigen Stress — früh handeln rettet oft die Saison.`;

  return (
    <div
      className="eid-card p-5 relative overflow-hidden"
      data-testid="impact-card"
      style={{
        background:
          "linear-gradient(135deg, #141414 0%, #181818 45%, #0E1A14 100%)",
      }}
    >
      <div className="flex items-center gap-2 mb-3">
        <Sparkle size={18} className="text-[#00FA9A]" weight="fill" />
        <div className="eid-label">Was das für dich heißt</div>
      </div>
      <div className="space-y-2 text-sm text-white leading-relaxed">
        <p>{ndviLine}</p>
        <p>{waterLine}</p>
        {yieldLine && <p>{yieldLine}</p>}
      </div>

      <div className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
        <div className="p-2 bg-black/50 rounded-sm" data-testid="impact-klima">
          <div className="flex items-center gap-1 text-[#00FA9A] font-medium">
            <Tree size={12} weight="fill" /> Klima
          </div>
          <div className="mt-1 text-zinc-400">
            Satelliten-Monitoring ersetzt ~4 Vor-Ort-Fahrten/Saison.
          </div>
        </div>
        <div className="p-2 bg-black/50 rounded-sm" data-testid="impact-gewinn">
          <div className="flex items-center gap-1 text-[#00FA9A] font-medium">
            <Coins size={12} weight="fill" /> Gewinn
          </div>
          <div className="mt-1 text-zinc-400">
            Frühwarnungen senken Ertragsrisiko um bis zu 15 %.
          </div>
        </div>
        <div className="p-2 bg-black/50 rounded-sm" data-testid="impact-tier">
          <div className="flex items-center gap-1 text-[#00FA9A] font-medium">
            <Butterfly size={12} weight="fill" /> Tier
          </div>
          <div className="mt-1 text-zinc-400">
            Gezielte Maßnahmen schonen Wildtiere & Bestäuber.
          </div>
        </div>
        <div className="p-2 bg-black/50 rounded-sm" data-testid="impact-wasser">
          <div className="flex items-center gap-1 text-[#00FA9A] font-medium">
            <Drop size={12} weight="fill" /> Wasser
          </div>
          <div className="mt-1 text-zinc-400">
            Bewässern nur wenn nötig — spart Trinkwasser & Kosten.
          </div>
        </div>
        <div className="p-2 bg-black/50 rounded-sm" data-testid="impact-kreislauf">
          <div className="flex items-center gap-1 text-[#00FA9A] font-medium">
            <Recycle size={12} weight="fill" /> Kreislauf
          </div>
          <div className="mt-1 text-zinc-400">
            Humusaufbau & stabile Böden = Zukunftsfähigkeit.
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Entdecken() {
  const [picked, setPicked] = useState(null);
  const [snap, setSnap] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = async (pickData) => {
    setPicked(pickData);
    setSnap(null);
    setError("");
    if (!pickData?.loc_id) {
      // PLZ-Pick → noch kein Snapshot-Endpoint für beliebige PLZ. Zeige Platzhalter + CTA.
      return;
    }
    setLoading(true);
    try {
      const data = await fetchLocationSnapshot(pickData.loc_id);
      setSnap(data);
    } catch (e) {
      setError("Momentan keine Daten verfügbar. Versuch es später erneut.");
    } finally {
      setLoading(false);
    }
  };

  const refresh = async () => {
    if (!picked?.loc_id) return;
    setLoading(true);
    try {
      const data = await fetchLocationSnapshot(picked.loc_id, true);
      setSnap(data);
    } finally {
      setLoading(false);
    }
  };

  const metrics = snap?.snapshot?.metrics || {};
  const ndviLevel = ndviAnomaly(metrics.ndvi_anomaly).level;
  const waterLevel = soilDeficitStatus(metrics.soil_moisture_deficit).level;
  const yLevel = yieldStatus(metrics.yield_forecast_index).level;

  return (
    <div className="space-y-6" data-testid="entdecken-page">
      <Helmet>
        <title>Entdecken · bodenblick.com</title>
      </Helmet>

      {/* Hero */}
      <div className="eid-card p-6 md:p-8 relative overflow-hidden" data-testid="entdecken-hero">
        <div
          className="absolute inset-0 opacity-30 pointer-events-none"
          style={{
            background:
              "radial-gradient(circle at 85% 15%, #0E1A14 0%, transparent 55%), radial-gradient(circle at 15% 90%, #141414 0%, transparent 55%)",
          }}
        />
        <div className="relative">
          <div className="flex items-center gap-2">
            <Compass size={22} className="text-[#00FA9A]" />
            <div className="eid-label">Entdecken</div>
          </div>
          <h1 className="mt-2 text-3xl md:text-4xl font-medium tracking-tight max-w-2xl">
            Deine Region. Dein Boden. In einem Blick.
          </h1>
          <p className="mt-3 text-sm md:text-base text-zinc-400 max-w-2xl leading-relaxed">
            Gib deine <strong>PLZ</strong> ein oder wähl deinen <strong>Landkreis / Bezirk</strong>.
            Wir holen Satellit, Wetter und Prognose live — und übersetzen die Zahlen
            in Klartext. Für Bauern, Förster:innen und alle, die ihr Land verstehen wollen.
          </p>
        </div>
      </div>

      {/* Picker */}
      <div className="eid-card p-5" data-testid="entdecken-picker-box">
        <LocationPicker onPick={load} testid="entdecken-picker" />
      </div>

      <DualExplainer
        title="Was siehst du hier — und woher kommen die Zahlen?"
        testid="entdecken-explainer"
        simple={
          <>
            <p>
              Such deine <strong>PLZ</strong> oder wähl deinen <strong>Landkreis / Bezirk</strong> —
              wir holen für genau diesen Punkt vier Live-Werte: wie grün deine Fläche ist
              (<GlossaryTerm word="NDVI">NDVI</GlossaryTerm>), wie viel Wasser im Boden steckt
              (<GlossaryTerm word="Bodenfeuchte">Bodenfeuchte</GlossaryTerm>), wie der
              Ernte-Ausblick aussieht und wie das Wetter für die nächsten 7 Tage wird.
            </p>
            <p>
              <strong>Farben verstehen:</strong> grün = alles im grünen Bereich · gelb =
              aufpassen · rot = Stress. Unten in der „Was das für dich heißt"-Karte übersetzen
              wir die Zahlen in einen Satz, den jeder versteht.
            </p>
            <p className="text-[#00FA9A]">
              <strong>Praxis-Tipp:</strong> Für eine genaue Analyse deines konkreten Feldes
              leg dir ein eigenes Feld an („Feld auf Karte zeichnen") — dort siehst du
              kleinräumige Unterschiede, die ein Landkreis-Mittel verschluckt.
            </p>
          </>
        }
        science={
          <>
            <p>
              <strong>Satellit:</strong> <GlossaryTerm word="Sentinel-2">Sentinel-2</GlossaryTerm>{" "}
              L2A, 10 m Auflösung, Wolken-maskiert, jüngstes verfügbares Acquisition-Date
              (Cloud-Cover &lt; 30 %). NDVI = (NIR − Rot) / (NIR + Rot), gemittelt über die
              Landkreis-/Bezirks-Bbox.
            </p>
            <p>
              <strong>Wetter & Boden:</strong> <GlossaryTerm word="Open-Meteo">Open-Meteo</GlossaryTerm>{" "}
              (ICON-EU / ECMWF-IFS Mix), Bodenfeuchte 0–10 cm, Bewässerungs-Bilanz aus
              ET₀-Niederschlag der letzten 5 Tage. Ernte-Index: Phänologie-Indikator
              0–100 aus GDD und NDVI-Trend.
            </p>
            <p>
              <strong>Cache:</strong> 6 h pro Landkreis/Bezirk — manuell auffrischen mit „Neu
              laden". Aggregation auf Verwaltungsgrenze schluckt Mikro-Heterogenität; für
              Schlag-Genauigkeit nutze „Mein Hof".
            </p>
          </>
        }
      />

      {/* Loading */}
      {loading && (
        <div className="eid-card p-6 flex items-center gap-3" data-testid="entdecken-loading">
          <CircleNotch size={20} className="animate-spin text-[#00FA9A]" />
          <div>
            <div className="font-medium">Hole aktuelle Daten für {picked?.label} …</div>
            <div className="text-xs text-zinc-500">
              Copernicus-Satellit + Open-Meteo. Ca. 10–20 Sekunden.
            </div>
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="eid-card p-4 text-sm text-[#FF7A45] bg-[#FF5500]/10" data-testid="entdecken-error">
          {error}
        </div>
      )}

      {/* No loc_id picked (free PLZ) */}
      {picked && !picked.loc_id && !loading && (
        <div className="eid-card p-5" data-testid="entdecken-plz-hint">
          <div className="flex items-start gap-3">
            <MapPin size={18} className="text-[#00FA9A] shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="font-medium">{picked.label} gefunden</div>
              <div className="text-sm text-zinc-400 mt-1">
                Deine PLZ liegt bei <span className="font-mono">{picked.lat.toFixed(4)}, {picked.lon.toFixed(4)}</span>.
                Für eine **volle Detail-Analyse** leg ein eigenes Feld an — dort bekommst du
                Satelliten-Snapshots, Bewässerungs-Ampel, Ernteplaner und Alerts.
              </div>
              <a
                href="/myfields"
                data-testid="entdecken-create-field-link"
                className="mt-3 inline-flex items-center gap-1 bg-[#00FA9A] text-black hover:bg-[#33FBAE] rounded-full px-4 py-2 text-sm"
              >
                Feld auf Karte zeichnen <ArrowRight size={14} />
              </a>
              <div className="text-[11px] text-zinc-500 mt-2">
                Oder wechsle oben zu „Aus Liste wählen" und pick deinen Landkreis.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Snapshot results */}
      {snap?.location && !loading && (
        <>
          <div className="eid-card p-5" data-testid="entdecken-result">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
              <div>
                <div className="eid-label">
                  {snap.location.country === "AT" ? "🇦🇹" : "🇩🇪"} {snap.location.bundesland}
                </div>
                <h2 className="text-2xl font-medium tracking-tight mt-1">
                  {snap.location.label}
                </h2>
              </div>
              <button
                onClick={refresh}
                data-testid="entdecken-refresh"
                className="text-xs flex items-center gap-1 border border-white/10 hover:bg-white/5 rounded-sm px-3 py-1.5"
              >
                <ArrowClockwise size={12} /> Neu laden
              </button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <StatCell
                icon={Plant}
                label="Vegetation (NDVI)"
                value={metrics.ndvi_latest != null ? metrics.ndvi_latest.toFixed(2) : "—"}
                level={ndviLevel}
              />
              <StatCell
                icon={Drop}
                label="Wasser-Bilanz"
                value={
                  metrics.soil_moisture_deficit == null
                    ? "—"
                    : metrics.soil_moisture_deficit <= 0
                      ? "Ausreichend"
                      : `-${(metrics.soil_moisture_deficit * 100).toFixed(1)} %`
                }
                level={waterLevel}
              />
              <StatCell
                icon={TrendUp}
                label="Ernte-Ausblick"
                value={metrics.yield_forecast_index != null ? `${metrics.yield_forecast_index}/100` : "—"}
                level={yLevel}
              />
              <StatCell
                icon={Sun}
                label="7-Tage-Trend"
                value={
                  (snap.snapshot.forecast?.days?.length || 0) > 0
                    ? `${snap.snapshot.forecast.days.length} Tage vor`
                    : "—"
                }
                level="ok"
                small
              />
            </div>
          </div>

          <ImpactCard snap={snap.snapshot} />

          {/* CTAs */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <a
              href="/myfields"
              data-testid="entdecken-cta-myfields"
              className="eid-card p-4 hover:bg-white/5 transition-colors"
            >
              <MapPin size={18} className="text-[#00FA9A]" />
              <div className="mt-2 font-medium">Eigenes Feld anlegen</div>
              <div className="text-xs text-zinc-400 mt-1">
                Zeichne dein Polygon auf der Karte — genauere Daten, Alerts & Ernteplaner.
              </div>
            </a>
            <a
              href="/planner"
              data-testid="entdecken-cta-planner"
              className="eid-card p-4 hover:bg-white/5 transition-colors"
            >
              <Sun size={18} className="text-[#00FA9A]" />
              <div className="mt-2 font-medium">Erntefenster prüfen</div>
              <div className="text-xs text-zinc-400 mt-1">
                Der Ernteplaner sagt dir auf den Tag genau, wann's passt.
              </div>
            </a>
            <a
              href="/commodities"
              data-testid="entdecken-cta-commodities"
              className="eid-card p-4 hover:bg-white/5 transition-colors"
            >
              <Coins size={18} className="text-[#00FA9A]" />
              <div className="mt-2 font-medium">Preise checken</div>
              <div className="text-xs text-zinc-400 mt-1">
                Weizen, Mais, Raps & Co — live aus den Futures-Märkten.
              </div>
            </a>
          </div>
        </>
      )}

      {/* Empty state */}
      {!picked && !loading && (
        <div className="eid-card p-6 text-center text-sm text-zinc-400" data-testid="entdecken-empty">
          <Heart size={24} className="mx-auto text-[#00FA9A]" weight="fill" />
          <div className="mt-2 font-medium text-white">
            Bereit, deinen Boden kennenzulernen?
          </div>
          <div className="mt-1">
            253 Landkreise & Bezirke in AT + DE warten. Oder such deine PLZ.
          </div>
        </div>
      )}
    </div>
  );
}
